import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  adminApi,
  ONBOARDING_STATUSES,
  type OnboardingStatus,
  type RetailerIndustry,
  type OnboardingJobSummary,
  type PartialGapAggregate,
} from '../../api/client';
import { SkeletonTable } from '../../components/SkeletonTable';
import { EmptyState } from '../../components/EmptyState';
import { JobStatusBadge } from '../../components/onboarding/JobStatusBadge';
import { OnboardingStepper } from '../../components/onboarding/OnboardingStepper';
import { InfoTooltip } from '../../components/InfoTooltip';
import { useToast } from '../../contexts/ToastContext';

const INDUSTRIES: { value: RetailerIndustry; label: string }[] = [
  { value: 'retail', label: 'Retail' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'home-goods', label: 'Home Goods' },
  { value: 'convenience', label: 'Convenience' },
  { value: 'travel-center', label: 'Travel Center' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'other', label: 'Other' },
];

export function AdminOnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [statusFilter, setStatusFilter] = useState<OnboardingStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Queries
  const { data: stats } = useQuery({
    queryKey: ['admin', 'onboarding', 'stats'],
    queryFn: () => adminApi.onboarding.stats(),
  });

  const { data: jobs, isLoading } = useQuery({
    queryKey: ['admin', 'onboarding', 'jobs', statusFilter],
    queryFn: () => adminApi.onboarding.list(statusFilter ? { status: statusFilter } : undefined),
    refetchInterval: 10_000, // Poll every 10s for status updates
  });

  const { data: gapsData } = useQuery({
    queryKey: ['admin', 'onboarding', 'partial-gaps'],
    queryFn: () => adminApi.onboarding.partialGaps(),
    enabled: showGaps,
  });

  const { data: diagnosticsData } = useQuery({
    queryKey: ['admin', 'onboarding', 'diagnostics'],
    queryFn: () => adminApi.onboarding.diagnostics(),
    enabled: showDiagnostics,
    refetchInterval: showDiagnostics ? 30_000 : false,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.onboarding.create>[0]) =>
      adminApi.onboarding.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      setShowCreateModal(false);
      addToast({ type: 'success', message: 'Onboarding job created' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const batchMutation = useMutation({
    mutationFn: (data: Parameters<typeof adminApi.onboarding.createBatch>[0]) =>
      adminApi.onboarding.createBatch(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      setShowBatchModal(false);
      addToast({ type: 'success', message: `${result.length} onboarding jobs created` });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) => adminApi.onboarding.retry(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: 'Job retry started' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const advanceMutation = useMutation({
    mutationFn: (jobId: string) => adminApi.onboarding.advance(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: 'Pipeline started — processing in background. Refresh to check progress.' });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  const queueMutation = useMutation({
    mutationFn: () => adminApi.onboarding.processQueue({ maxJobs: 10, delayMs: 0 }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      addToast({ type: 'success', message: `Queue: ${result.processed} processed, ${result.skipped} skipped` });
    },
    onError: (err: Error) => addToast({ type: 'error', message: err.message }),
  });

  // Filters
  const filteredJobs = (jobs ?? []).filter((j) => {
    if (searchQuery && !j.partnerName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const sortedJobs = [...filteredJobs].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const specUrlsRaw = (fd.get('specUrls') as string || '').trim();
    const files = (fd.getAll('specFiles') as File[]).filter((f) => f.size > 0);

    try {
      const job = await adminApi.onboarding.create({
        partnerName: fd.get('partnerName') as string,
        industryHint: (fd.get('industryHint') as RetailerIndustry) || undefined,
        specUrls: specUrlsRaw ? specUrlsRaw.split('\n').map((u) => u.trim()).filter(Boolean) : undefined,
        createdBy: 'admin',
      });

      // Upload files if any were selected
      if (files.length > 0) {
        setUploadingFiles(true);
        for (const file of files) {
          await adminApi.onboarding.uploadSpec(job.jobId, file);
        }
        setUploadingFiles(false);
      }

      queryClient.invalidateQueries({ queryKey: ['admin', 'onboarding'] });
      setShowCreateModal(false);
      addToast({ type: 'success', message: `Onboarding job created${files.length ? ` with ${files.length} spec file(s)` : ''}` });
    } catch (err) {
      setUploadingFiles(false);
      addToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to create job' });
    }
  };

  const handleBatch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const lines = (fd.get('partners') as string || '').trim().split('\n').filter(Boolean);
    const partners = lines.map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      return {
        name: parts[0],
        industryHint: (parts[1] as RetailerIndustry) || undefined,
      };
    });
    if (partners.length === 0) return;
    batchMutation.mutate({ partners, createdBy: 'admin' });
  };

  // Stat helpers
  const total = stats?.total ?? 0;
  const byStat = stats?.byStatus ?? {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Onboarding</h1>
          <p className="text-sm text-gray-500">AI-powered autonomous partner onboarding pipeline</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => setShowDiagnostics(!showDiagnostics)}>
            {showDiagnostics ? 'Hide Diagnostics' : 'Diagnostics'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowGaps(!showGaps)}>
            {showGaps ? 'Hide Gaps' : 'Partial Gaps'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => queueMutation.mutate()}
            disabled={queueMutation.isPending || (byStat.queued ?? 0) === 0}
            title="Starts processing all queued jobs. Jobs advance through: sourcing → extraction → review → composition → review → provisioning."
          >
            {queueMutation.isPending ? 'Processing...' : `Process Queue (${byStat.queued ?? 0})`}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowBatchModal(true)}>
            Batch Import
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + New Partner
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={total} color="text-gray-900" />
        <StatCard label="Queued" value={byStat.queued ?? 0} color="text-gray-600" />
        <StatCard label="In Progress" value={(byStat.sourcing ?? 0) + (byStat.extracting ?? 0) + (byStat.composing ?? 0) + (byStat.provisioning ?? 0)} color="text-blue-600" />
        <StatCard label="Review" value={(byStat.review_specs ?? 0) + (byStat.review_labels ?? 0)} color="text-yellow-600" />
        <StatCard label="Completed" value={byStat.completed ?? 0} color="text-green-600" />
        <StatCard label="Failed" value={byStat.failed ?? 0} color="text-red-600" />
      </div>

      {/* Pipeline Diagnostics */}
      {showDiagnostics && diagnosticsData && (
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Pipeline Diagnostics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Agent Status */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent Status</h3>
              <ul className="space-y-1 text-sm">
                {Object.entries(diagnosticsData.agents).map(([key, ok]) => (
                  <li key={key} className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-gray-700">{key}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Env Vars */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Required Environment</h3>
              <ul className="space-y-1 text-sm">
                {Object.entries(diagnosticsData.envVars).map(([key, val]) => (
                  <li key={key} className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${val ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-gray-700 text-xs">{key}</span>
                  </li>
                ))}
              </ul>
              {diagnosticsData.optionalVars && (
                <>
                  <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Optional</h3>
                  <ul className="space-y-1 text-sm">
                    {Object.entries(diagnosticsData.optionalVars).map(([key, val]) => (
                      <li key={key} className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${val ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-gray-500 text-xs">{key}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {diagnosticsData.info && (
                <div className="mt-3 text-xs text-gray-400 space-y-0.5">
                  {Object.entries(diagnosticsData.info).map(([key, val]) => (
                    <div key={key}>{key}: {String(val)}</div>
                  ))}
                </div>
              )}
            </div>
            {/* Pipeline Ready */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Pipeline</h3>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${diagnosticsData.pipelineReady ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {diagnosticsData.pipelineReady ? '✓ Ready' : '✗ Not Ready'}
              </div>
              {diagnosticsData.circuitBreaker && (
                <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${diagnosticsData.circuitBreaker.isOpen ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  Circuit Breaker: {diagnosticsData.circuitBreaker.isOpen ? 'OPEN' : 'Closed'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Partial Gaps Section */}
      {showGaps && (
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Partial Gap Analysis</h2>
          <p className="text-sm text-gray-500 mb-4">
            Requirements across onboarding jobs that have no matching partial.
            Higher numbers indicate high-value partials to create.
          </p>
          {(gapsData?.gaps ?? []).length === 0 ? (
            <p className="text-sm text-gray-400">No partial gaps detected yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Requirement</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Occurrences</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Affected Partners</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Suggested</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {gapsData!.gaps.map((gap: PartialGapAggregate, i: number) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{gap.requirement}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        {gap.occurrences}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs">
                      {gap.affectedPartners.join(', ')}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{gap.suggestedApproach.replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex gap-3 items-center">
        <select
          className="input w-48"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as OnboardingStatus | '')}
        >
          <option value="">All Statuses</option>
          {ONBOARDING_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search partner name..."
          className="input flex-1"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Jobs table */}
      {isLoading ? (
        <SkeletonTable rows={8} columns={6} />
      ) : sortedJobs.length === 0 ? (
        <EmptyState
          icon="🚀"
          title="No onboarding jobs"
          description="Create a new partner onboarding job to get started."
          actionLabel="+ New Partner"
          onAction={() => setShowCreateModal(true)}
        />
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Partner</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Industry</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Progress</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Updated</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedJobs.map((job) => (
                <JobRow
                  key={job.jobId}
                  job={job}
                  onClick={() => navigate(`/admin/onboarding/${job.jobId}`)}
                  onRetry={() => retryMutation.mutate(job.jobId)}
                  onAdvance={() => advanceMutation.mutate(job.jobId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="New Partner Onboarding" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partner Name *</label>
              <input name="partnerName" className="input w-full" required placeholder="e.g., Walmart" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry (optional)</label>
              <select name="industryHint" className="input w-full">
                <option value="">Auto-detect</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spec URLs (one per line, optional)
                <InfoTooltip text="If you know where the retailer's label specifications are published, add URLs here. Otherwise, the AI will search for them." />
              </label>
              <textarea name="specUrls" className="input w-full" rows={3} placeholder="https://partner.com/edi-guide.pdf" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload Spec Files (optional)
                <InfoTooltip text="Upload PDF, Word, or Excel spec documents directly. Use this when the spec is behind a login or not publicly accessible." />
              </label>
              <input
                name="specFiles"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xlsx,.html,.png,.jpg,.jpeg"
                className="input w-full text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, HTML, or images. Max 20 MB per file.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || uploadingFiles}>
                {uploadingFiles ? 'Uploading files...' : createMutation.isPending ? 'Creating...' : 'Create Job'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Batch Modal */}
      {showBatchModal && (
        <Modal title="Batch Import Partners" onClose={() => setShowBatchModal(false)}>
          <form onSubmit={handleBatch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partners (one per line, format: <code className="text-xs">name, industry</code>)
              </label>
              <textarea
                name="partners"
                className="input w-full font-mono text-sm"
                rows={8}
                required
                placeholder={"Walmart, retail\nTarget, retail\nCostco, warehouse\nHome Depot, home-goods"}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBatchModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={batchMutation.isPending}>
                {batchMutation.isPending ? 'Creating...' : 'Create Jobs'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ---- Sub-components ----

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-lg border p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function JobRow({
  job,
  onClick,
  onRetry,
  onAdvance,
}: {
  job: OnboardingJobSummary;
  onClick: () => void;
  onRetry: () => void;
  onAdvance: () => void;
}) {
  return (
    <tr className="hover:bg-gray-50 cursor-pointer" onClick={onClick}>
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{job.partnerName}</div>
        <div className="text-xs text-gray-400">{job.partnerSlug}</div>
      </td>
      <td className="px-4 py-3 text-gray-600 capitalize">{job.industryHint?.replace(/-/g, ' ') ?? '—'}</td>
      <td className="px-4 py-3">
        <JobStatusBadge status={job.status} />
        {job.failedReason && (
          <p className="text-xs text-red-500 mt-0.5 truncate max-w-[180px]" title={job.failedReason}>
            {job.failedReason}
          </p>
        )}
      </td>
      <td className="px-4 py-3 hidden lg:table-cell">
        <div className="w-48">
          <OnboardingStepper status={job.status} />
        </div>
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
        {new Date(job.updatedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1">
          {job.status === 'queued' && (
            <button className="text-xs text-blue-600 hover:underline" onClick={onAdvance}>
              Start
            </button>
          )}
          {job.status === 'failed' && (
            <button className="text-xs text-orange-600 hover:underline" onClick={onRetry}>
              Retry
            </button>
          )}
          <button className="text-xs text-primary-600 hover:underline" onClick={onClick}>
            View
          </button>
        </div>
      </td>
    </tr>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button className="text-gray-400 hover:text-gray-600 text-xl" onClick={onClose}>×</button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}
